import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Play, TrendingUp, AlertCircle } from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import { runSensitivityAnalysis, calculateCapacity } from '@/lib/calculations';

const PARAMETERS = [
  { value: 'avgQu', label: 'Average qu (scale all layers)', unit: '×' },
  { value: 'pileDiameter', label: 'Pile Diameter', unit: '×' },
  { value: 'groutLength', label: 'Grout Length', unit: '×' },
  { value: 'groutStrength', label: 'Grout Strength', unit: '×' },
  { value: 'avgTrg', label: 'Average T_RG (scale all layers)', unit: '×' }
];

export const SensitivityTab = ({ pileInputs, soilLayers, sensitivityResults, setSensitivityResults, chartRef }) => {
  const [parameter, setParameter] = useState('avgQu');
  const [fromValue, setFromValue] = useState(0.5);
  const [toValue, setToValue] = useState(1.5);
  const [steps, setSteps] = useState(11);
  const [isRunning, setIsRunning] = useState(false);

  // Get base results for comparison
  const baseInputs = { ...pileInputs, soilLayers };
  const baseResults = calculateCapacity(baseInputs);

  // Run sensitivity analysis
  const handleRunSensitivity = () => {
    setIsRunning(true);
    
    try {
      const results = runSensitivityAnalysis(
        baseInputs,
        parameter,
        parseFloat(fromValue),
        parseFloat(toValue),
        parseInt(steps),
        true // Use multipliers
      );
      
      setSensitivityResults(results);
    } catch (error) {
      console.error('Sensitivity analysis error:', error);
    } finally {
      setIsRunning(false);
    }
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white dark:bg-slate-900 border border-border rounded-lg shadow-lg p-3 text-sm">
          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
            <span className="text-muted-foreground">Multiplier:</span>
            <span className="font-mono font-medium">{data.multiplier.toFixed(2)}×</span>
            <span className="text-muted-foreground">Ultimate:</span>
            <span className="font-mono font-medium">{data.ultimateCapacity} MN</span>
            <span className="text-muted-foreground">Allowable:</span>
            <span className="font-mono font-medium">{data.allowableCapacity} MN</span>
            <span className="text-muted-foreground">Governing:</span>
            <span className="text-xs">{data.governingComponent}</span>
          </div>
        </div>
      );
    }
    return null;
  };

  // Get parameter label
  const getParameterLabel = () => {
    const param = PARAMETERS.find(p => p.value === parameter);
    return param ? param.label : parameter;
  };

  return (
    <div className="space-y-6" data-testid="sensitivity-tab">
      {/* Control Panel */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Sensitivity Analysis
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
            {/* Parameter Selection */}
            <div className="form-group md:col-span-2">
              <Label htmlFor="parameter" className="form-label">Parameter to Vary</Label>
              <Select value={parameter} onValueChange={setParameter}>
                <SelectTrigger data-testid="sensitivity-parameter-select">
                  <SelectValue placeholder="Select parameter" />
                </SelectTrigger>
                <SelectContent>
                  {PARAMETERS.map(param => (
                    <SelectItem key={param.value} value={param.value}>
                      {param.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* From Value */}
            <div className="form-group">
              <Label htmlFor="fromValue" className="form-label">From (multiplier)</Label>
              <Input
                id="fromValue"
                type="number"
                step="0.1"
                value={fromValue}
                onChange={(e) => setFromValue(e.target.value)}
                className="engineering-input"
                data-testid="sensitivity-from-input"
              />
            </div>

            {/* To Value */}
            <div className="form-group">
              <Label htmlFor="toValue" className="form-label">To (multiplier)</Label>
              <Input
                id="toValue"
                type="number"
                step="0.1"
                value={toValue}
                onChange={(e) => setToValue(e.target.value)}
                className="engineering-input"
                data-testid="sensitivity-to-input"
              />
            </div>

            {/* Steps */}
            <div className="form-group">
              <Label htmlFor="steps" className="form-label">Steps</Label>
              <Input
                id="steps"
                type="number"
                min="3"
                max="50"
                value={steps}
                onChange={(e) => setSteps(e.target.value)}
                className="engineering-input"
                data-testid="sensitivity-steps-input"
              />
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Varies <strong>{getParameterLabel()}</strong> from {fromValue}× to {toValue}× of current value in {steps} steps
            </p>
            <Button 
              onClick={handleRunSensitivity} 
              disabled={isRunning}
              className="bg-[#00284F] hover:bg-[#00284F]/90"
              data-testid="run-sensitivity-btn"
            >
              <Play className="h-4 w-4 mr-2" />
              {isRunning ? 'Running...' : 'Run Sensitivity'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Base Case Reference */}
      <Card className="border-dashed bg-muted/20">
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 mb-3">
            <AlertCircle className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Base Case (1.0× multiplier)</span>
          </div>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-xl font-bold font-mono text-[#00284F] dark:text-blue-400">
                {baseResults.ultimateCapacity.toFixed(3)}
              </div>
              <div className="text-xs text-muted-foreground">Ultimate (MN)</div>
            </div>
            <div>
              <div className="text-xl font-bold font-mono">
                {baseResults.allowableCapacity.toFixed(3)}
              </div>
              <div className="text-xs text-muted-foreground">Allowable (MN)</div>
            </div>
            <div>
              <div className="text-xl font-bold font-mono">
                {baseResults.shaftCapacity.toFixed(3)}
              </div>
              <div className="text-xs text-muted-foreground">Shaft (MN)</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {sensitivityResults && sensitivityResults.length > 0 ? (
        <>
          {/* Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">
                Sensitivity Chart: {getParameterLabel()}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[400px]" data-testid="sensitivity-chart" ref={chartRef}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={sensitivityResults}
                    margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#94a3b8" />
                    <XAxis
                      dataKey="multiplier"
                      stroke="#64748b"
                      fontSize={12}
                      tickFormatter={(v) => `${v.toFixed(1)}×`}
                      label={{ value: 'Multiplier', position: 'bottom', offset: 0 }}
                    />
                    <YAxis
                      stroke="#64748b"
                      fontSize={12}
                      width={60}
                      label={{ value: 'Capacity (MN)', angle: -90, position: 'insideLeft', offset: 10 }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <ReferenceLine
                      x={1}
                      stroke="#f97316"
                      strokeDasharray="5 5"
                      label={{ value: 'Base', position: 'top', fill: '#f97316', fontSize: 11 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="ultimateCapacity"
                      name="Ultimate"
                      stroke="#00284F"
                      strokeWidth={2}
                      dot={{ r: 3, fill: '#00284F' }}
                      activeDot={{ r: 5 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="allowableCapacity"
                      name="Allowable"
                      stroke="#10b981"
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      dot={{ r: 2, fill: '#10b981' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Results Table */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Sensitivity Results Table</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-center">Step</TableHead>
                      <TableHead className="text-right">Multiplier</TableHead>
                      <TableHead className="text-right">Ultimate (MN)</TableHead>
                      <TableHead className="text-right">Allowable (MN)</TableHead>
                      <TableHead className="text-right">Shaft (MN)</TableHead>
                      <TableHead>Governing</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sensitivityResults.map((result, index) => (
                      <TableRow 
                        key={index}
                        className={result.multiplier === 1 ? 'bg-[#00284F]/10' : ''}
                        data-testid={`sensitivity-row-${index}`}
                      >
                        <TableCell className="text-center font-mono">{result.step}</TableCell>
                        <TableCell className="text-right font-mono">
                          {result.multiplier.toFixed(2)}×
                          {result.multiplier === 1 && (
                            <span className="ml-2 text-xs bg-[#00284F] text-white px-1.5 py-0.5 rounded">BASE</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-mono font-semibold">
                          {result.ultimateCapacity.toFixed(3)}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {result.allowableCapacity.toFixed(3)}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {result.shaftCapacity.toFixed(3)}
                        </TableCell>
                        <TableCell className="text-xs">
                          {result.governingComponent}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Summary Statistics */}
          <Card className="bg-muted/30">
            <CardContent className="pt-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div>
                  <div className="text-xl font-bold font-mono text-red-500">
                    {Math.min(...sensitivityResults.map(r => r.ultimateCapacity)).toFixed(3)}
                  </div>
                  <div className="text-xs text-muted-foreground">Min Ultimate (MN)</div>
                </div>
                <div>
                  <div className="text-xl font-bold font-mono text-green-500">
                    {Math.max(...sensitivityResults.map(r => r.ultimateCapacity)).toFixed(3)}
                  </div>
                  <div className="text-xs text-muted-foreground">Max Ultimate (MN)</div>
                </div>
                <div>
                  <div className="text-xl font-bold font-mono">
                    {(Math.max(...sensitivityResults.map(r => r.ultimateCapacity)) - 
                      Math.min(...sensitivityResults.map(r => r.ultimateCapacity))).toFixed(3)}
                  </div>
                  <div className="text-xs text-muted-foreground">Range (MN)</div>
                </div>
                <div>
                  <div className="text-xl font-bold font-mono">
                    {((Math.max(...sensitivityResults.map(r => r.ultimateCapacity)) / 
                       Math.min(...sensitivityResults.map(r => r.ultimateCapacity)) - 1) * 100).toFixed(1)}%
                  </div>
                  <div className="text-xs text-muted-foreground">Variation</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      ) : (
        <Card className="border-dashed">
          <CardContent className="py-16 text-center">
            <TrendingUp className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium">No Sensitivity Results</h3>
            <p className="text-muted-foreground mt-1">
              Configure parameters above and click "Run Sensitivity" to analyze
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
